import { Event as EventIcon, EventAvailable, EventBusy } from '@mui/icons-material';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useEventAccess } from '../../../../../../../hooks/useEventAccess';
import { usePresentationBooking } from '../../../../../../../hooks/usePresentationBooking';
import { Slot } from '../../../../../../../interfaces/interfaces';
import { setPostRegistrationAction } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../../../reduxToolkit/typedRedux';
import { VyConfirm } from '../../../../../../common/VyConfirm';
import VyTooltip from '../../../../../../common/VyTooltip';

interface PresentationBookingButtonProps {
  slot: Slot;
  customHtmlColor?: string;
}

export const PresentationBookingButton = ({ slot, customHtmlColor }: PresentationBookingButtonProps) => {
  const {
    getPresentationType,
    getPresentationTicketState,
    getPresentationButtonState,
    bookPresentationTicket,
    cancelPresentationTicket,
    initPresentationTickets,
  } = usePresentationBooking();
  const { loadUserData } = useEventAccess();
  const { t } = useTranslation('event');
  const dispatch = useDispatch();

  const event = useSelector((state) => state.common.event);
  const registration = useSelector((state) => state.common.eventRegistration);

  async function bookTicket() {
    try {
      await bookPresentationTicket(slot._id);
    } catch (err) {
      initPresentationTickets();
    }
  }
  async function handleTicketBook() {
    if (presentationButtonState === 'ACTIVE') await bookTicket();
    else
      dispatch(
        setPostRegistrationAction(async () => {
          loadUserData();
          await bookTicket();
        })
      );
  }
  async function handleTicketCancel() {
    try {
      await cancelPresentationTicket(slot._id);
    } catch (err) {
      console.error(err);
    }
  }

  const presentationType = getPresentationType(slot);
  const presentationTicketState = getPresentationTicketState(slot);
  const presentationButtonState = getPresentationButtonState(slot);
  if (
    !presentationType ||
    (presentationType === 'online' && !event?.settings.enableOnlinePresentationBooking) ||
    !!slot.meetingroom.liveStream ||
    presentationButtonState === 'HIDDEN'
  )
    return null;

  if (!registration)
    return (
      <VyTooltip title={t('presentation-ticket.not-registered')}>
        <div className="cursor-default">
          <EventIcon htmlColor="grey" />
        </div>
      </VyTooltip>
    );

  if (presentationTicketState === 'AVAILABLE')
    return (
      <VyTooltip title={t('presentation-ticket.book-tooltip')}>
        <VyConfirm
          title={t('presentation-ticket.confirm-booking-title')}
          message={t('presentation-ticket.confirm-booking-description')}
          skipConfirmation={presentationButtonState === 'REQUIRE_SIGNUP'}
          onConfirm={handleTicketBook}>
          <EventIcon htmlColor={customHtmlColor ?? 'white'} />
        </VyConfirm>
      </VyTooltip>
    );

  if (presentationTicketState === 'OWN')
    return (
      <>
        <VyTooltip title={t('presentation-ticket.own-ticket-tooltip')}>
          <VyConfirm
            title={t('presentation-ticket.confirm-cancelation-title')}
            message={t('presentation-ticket.confirm-cancel-description')}
            onConfirm={handleTicketCancel}>
            <div>
              <EventAvailable htmlColor={event?.template.complementColor ?? customHtmlColor ?? 'white'} />
            </div>
          </VyConfirm>
        </VyTooltip>
      </>
    );

  return (
    <VyTooltip title={t('presentation-ticket.ticket-unavailable')}>
      <div className="cursor-default">
        <EventBusy htmlColor="grey" />
      </div>
    </VyTooltip>
  );
};
